// DevHub CUSTOM — command-palette command registry.
//
// The Cmd+K modal is a command palette layered ABOVE the live Backstage search.
// This module owns the "commands" layer: navigable route destinations + a small,
// safe set of actions (theme toggle, open settings, open changelog). Everything
// here is pure data + pure functions so it's trivially unit-testable and carries
// no React/DOM dependencies — the modal wires the `run` callbacks to real effects.

import type { NavigateFunction } from 'react-router-dom';


/**
 * Context passed to each command's `run` (and `visible`) so commands stay pure.
 * The modal supplies concrete implementations; commands never reach for globals.
 */
export interface CommandContext {
  navigate: NavigateFunction;
  /** Close the palette (usually called right after navigating / acting). */
  close: () => void;
  /** Flip the app theme light↔dark. */
  toggleTheme: () => void;
  /** True when the current theme is dark (drives the theme-toggle label/icon). */
  isDark: boolean;
  /** True when the signed-in user may see admin destinations. */
  isAdmin: boolean;
  /** Record an opened item into the recent list. */
  pushRecent: (item: RecentCommand) => void;
}

/** A command the user can navigate to (a route) or invoke (a safe action). */
export interface Command {
  id: string;
  kind: 'route' | 'action';
  /** i18n key into searchMessages (the `palette.*` namespace) for the title. */
  titleKey: string;
  /** Optional i18n key for a secondary line (e.g. the route path context). */
  subtitleKey?: string;
  /** ODX core icon name (must be registered in index.tsx to render). */
  icon: string;
  /** Extra plain-text terms the fuzzy matcher considers (aliases, synonyms). */
  keywords: string[];
  /** Perform the command. Routes navigate + close; actions do their thing. */
  run: (ctx: CommandContext) => void;
  /** Optional gate — when it returns false the command is omitted. */
  visible?: (ctx: CommandContext) => boolean;
}

/** The minimal shape persisted to localStorage for the "Recent" group. */
export interface RecentCommand {
  /** Command id (route/action) OR a synthetic id for opened entities. */
  id: string;
  /** Display title — already resolved (recents don't re-translate). */
  title: string;
  icon: string;
  /** Route to navigate to when the recent entry is re-activated. */
  path: string;
}

// ─── Route destinations ──────────────────────────────────────────────────────
// Mirrors the primary IA nav (Root.tsx `sections`) plus the secondary destinations
// reachable from the header/menus. Paths match the live routes in App.tsx.
//
// A tiny helper builds a route command: navigating records the entry as "recent"
// so the user can jump back to it, then closes the palette.
function routeCommand(
  id: string,
  path: string,
  icon: string,
  keywords: string[],
  opts: { adminOnly?: boolean } = {},
): Command {
  const titleKey = `palette.nav.${id}`;
  return {
    id: `route.${id}`,
    kind: 'route',
    titleKey,
    subtitleKey: 'palette.group.navigate',
    icon,
    keywords,
    visible: opts.adminOnly ? ctx => ctx.isAdmin : undefined,
    run: ctx => {
      ctx.pushRecent({ id: `route.${id}`, title: titleKey, icon, path });
      ctx.navigate(path);
      ctx.close();
    },
  };
}

// ─── Actions ───────────────────────────────────────────────────────────────
// Deliberately small + safe: nothing mutates server state. Theme toggle, open
// settings, open the changelog.
const actionCommands: Command[] = [
  {
    id: 'action.toggle-theme',
    kind: 'action',
    // Title flips with the current theme, resolved in the modal via a special key.
    titleKey: 'palette.action.toggleTheme',
    subtitleKey: 'palette.group.actions',
    icon: 'core::night-mode',
    keywords: ['theme', 'dark', 'light', 'mode', 'appearance', 'contrast'],
    run: ctx => {
      ctx.toggleTheme();
      ctx.close();
    },
  },
  {
    id: 'action.open-settings',
    kind: 'action',
    titleKey: 'palette.action.openSettings',
    subtitleKey: 'palette.group.actions',
    icon: 'core::settings',
    keywords: ['settings', 'preferences', 'options', 'account', 'profile'],
    run: ctx => {
      ctx.navigate('/settings');
      ctx.close();
    },
  },
  {
    id: 'action.open-changelog',
    kind: 'action',
    titleKey: 'palette.action.openChangelog',
    subtitleKey: 'palette.group.actions',
    icon: 'core::history',
    keywords: ['changelog', 'release', 'notes', 'news', 'updates', 'version', 'whats new'],
    run: ctx => {
      ctx.navigate('/announcements');
      ctx.close();
    },
  },
];

/**
 * Build the full command set for the given context (applies visibility gates).
 * Route commands come first (they dominate the "Navigate" group), actions last.
 */
export function buildCommands(ctx: CommandContext): Command[] {
  const routes: Command[] = [
    routeCommand('home', '/', 'core::home', ['home', 'start', 'dashboard', 'landing']),
    routeCommand('catalog', '/catalog-overview', 'core::products', [
      'catalog', 'services', 'components', 'systems', 'software', 'entities',
    ]),
    routeCommand('docs', '/docs-overview', 'core::document', [
      'docs', 'documentation', 'techdocs', 'guides', 'manuals',
    ]),
    routeCommand('guides', '/docs/default/component/devhub-guides', 'core::training', [
      'guides', 'howto', 'tutorials', 'getting started', 'walkthrough',
    ]),
    routeCommand('resources', '/resources', 'core::toolbox', [
      'resources', 'tools', 'links', 'downloads',
    ]),
    routeCommand('selfService', '/self-service', 'core::service', [
      'self service', 'devsecops', 'create', 'scaffolder', 'pipeline', 'onboard',
    ]),
    // Tech Radar — out of this release (TECH_RADAR_ENABLED), so the palette does not
    // offer it at all: its route renders the 404 for everyone, admins included. When the
    // radar ships, the admin gate on the route comes back with it.
    routeCommand('landscape', '/landscape', 'core::map', [
      'landscape', 'technology landscape', 'map', 'overview',
    ]),
    routeCommand('news', '/announcements', 'core::notification', [
      'news', 'announcements', 'changelog', 'updates', 'releases',
    ]),
    routeCommand('community', '/community', 'core::group', [
      'community', 'people', 'teams', 'support', 'help',
    ]),
    routeCommand('mcpCatalog', '/mcp-catalog', 'core::object', [
      'mcp', 'mcp catalog', 'model context protocol', 'servers', 'ai tools',
    ]),
    routeCommand('settings', '/settings', 'core::settings', [
      'settings', 'preferences', 'account', 'profile', 'language', 'theme',
    ]),
    // Admin destinations — only visible to admins.
    routeCommand('adminOverview', '/admin', 'core::config', [
      'admin', 'overview', 'management', 'administration',
    ], { adminOnly: true }),
    routeCommand('adminAnalytics', '/admin/insights', 'core::dashboard', [
      'admin', 'analytics', 'insights', 'impact', 'usage', 'metrics', 'scoreboard',
    ], { adminOnly: true }),
    routeCommand('adminCuration', '/admin/curation', 'core::config-edit', [
      'admin', 'curation', 'content', 'radar', 'mcp',
    ], { adminOnly: true }),
    routeCommand('adminAccess', '/admin/access', 'core::group', [
      'admin', 'access', 'roles', 'permissions', 'tiers', 'users',
    ], { adminOnly: true }),
  ];

  return [...routes, ...actionCommands].filter(
    cmd => (cmd.visible ? cmd.visible(ctx) : true),
  );
}

// ─── Fuzzy matching ──────────────────────────────────────────────────────────
// A small, dependency-free subsequence matcher: the query characters must appear
// in order (not necessarily contiguously) somewhere in the haystack. Scores favour
// contiguous / prefix / word-start hits so the best command floats to the top.

/** Lowercased subsequence test with a relevance score, or -1 for no match. */
function fuzzyScore(query: string, haystack: string): number {
  const q = query.toLowerCase();
  const h = haystack.toLowerCase();
  if (!q) return 0;

  // Fast paths: exact / prefix / substring get big, well-ordered bonuses.
  if (h === q) return 1000;
  if (h.startsWith(q)) return 800;
  const idx = h.indexOf(q);
  if (idx >= 0) {
    // Contiguous substring — earlier and at a word boundary scores higher.
    const boundary = idx === 0 || h[idx - 1] === ' ' || h[idx - 1] === '-';
    return 500 + (boundary ? 100 : 0) - Math.min(idx, 50);
  }

  // Subsequence fallback — every query char appears in order.
  let hi = 0;
  let score = 0;
  let streak = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const ch = q[qi];
    let found = -1;
    for (let k = hi; k < h.length; k++) {
      if (h[k] === ch) { found = k; break; }
    }
    if (found === -1) return -1;
    // Reward contiguous runs and word-boundary landings.
    streak = found === hi ? streak + 1 : 0;
    const boundary = found === 0 || h[found - 1] === ' ' || h[found - 1] === '-';
    score += 10 + streak * 5 + (boundary ? 8 : 0);
    hi = found + 1;
  }
  return score;
}

// Minimum score for a command to appear in the palette. fuzzyScore awards ≥ 500 for
// a contiguous substring hit (exact = 1000, prefix = 800), and only double-digit /
// low-hundred scores for the scattered-subsequence fallback. Requiring 450 keeps
// substring-or-better matches and drops junk like "son" matching "admini{s}trati{o}{n}",
// "permis{s}i{on}s" or "ver{son}" — the loose subsequence hits that surfaced unrelated
// admin/changelog commands. Users still reach every command by a real word or prefix
// (titles + the per-command keyword synonyms are all matched).
const MIN_MATCH_SCORE = 450;
// Cap the palette group so it never becomes a scroll-in-a-scroll above the results.
const MAX_MATCHES = 6;

/**
 * Rank commands against a search term. `resolveTitle` turns each command's
 * titleKey into the displayed (translated) label so matching honours the active
 * language; keywords are always matched too. Returns the best-matching commands
 * (substring-or-better only), sorted best-first and capped to MAX_MATCHES.
 */
export function matchCommands(
  term: string,
  commands: Command[],
  resolveTitle: (cmd: Command) => string,
): Command[] {
  const trimmed = term.trim();
  if (!trimmed) return [];

  const scored = commands
    .map(cmd => {
      const haystacks = [resolveTitle(cmd), ...cmd.keywords];
      const best = haystacks.reduce(
        (max, h) => Math.max(max, fuzzyScore(trimmed, h)),
        -1,
      );
      return { cmd, score: best };
    })
    .filter(entry => entry.score >= MIN_MATCH_SCORE);

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, MAX_MATCHES).map(entry => entry.cmd);
}
